<!-- HeaderComponent.vue -->
<!-- HeaderComponent.vue -->
<template>
  <header
    ref="header"
    :style="{ backgroundColor: headerBackground, backdropFilter: isIndexPage && isScrolled ? 'blur(10px)' : 'none' }"
    class="header"
  >
    <div class="container">
      <!-- 로고 영역 -->
      <div class="logo">
        <router-link to="/">
          <img :src="logoSrc" alt="AirSignal Logo" height="30" />
        </router-link>
      </div>
      <!-- 메뉴 영역 -->
      <nav class="menu">
        <ul class="menu-list">
          <li><a :style="{ color: textColor }" href="/about">회사소개</a></li>
          <li><a :style="{ color: textColor }" href="/battery">배터리</a></li>
          <li><a :style="{ color: textColor }" href="/headset">헤드셋</a></li>
          <li><a :style="{ color: textColor }" href="/handset">핸드셋</a></li>
          <li><a :style="{ color: textColor }" href="/poweramp">PA</a></li>
          <li><a :style="{ color: textColor }" href="/antenna">안테나</a></li>
        </ul>
        <!-- 햄버거 메뉴 버튼 -->
        <div class="hamburger-menu" @click="toggleMobileMenu">
          <span :style="{ backgroundColor: hamburgerMenuColor }"></span>
          <span :style="{ backgroundColor: hamburgerMenuColor }"></span>
          <span :style="{ backgroundColor: hamburgerMenuColor }"></span>
        </div>
      </nav>
    </div>
  </header>
  <!-- 모달 메뉴 -->
  <transition name="modal">
    <div class="modal-menu" v-if="isMobileMenuOpen">
      <button class="close-button" @click="toggleMobileMenu">&times;</button>
      <ul class="modal-menu-list">
        <li><a :style="{ color: '#fff' }" href="/about">회사소개</a></li>
        <li><a :style="{ color: '#fff' }" href="/battery">배터리</a></li>
        <li><a :style="{ color: '#fff' }" href="/headset">헤드셋</a></li>
        <li><a :style="{ color: '#fff' }" href="/handset">핸드셋</a></li>
        <li><a :style="{ color: '#fff' }" href="/poweramp">PA</a></li>
        <li><a :style="{ color: '#fff' }" href="/antenna">안테나</a></li>
      </ul>
    </div>
  </transition>
</template>


<script>
import { ref, computed, onMounted, onBeforeUnmount, watch } from 'vue';
import { useRoute } from 'vue-router';
import { gsap } from 'gsap';
import { ScrollTrigger } from 'gsap/ScrollTrigger';
import asLogo from '@/assets/as_logo.svg';
import asLogoWhite from '@/assets/as_logo_white.svg';

export default {
  name: "HeaderComponent",
  setup() {
    gsap.registerPlugin(ScrollTrigger);

    const route = useRoute();
    const isIndexPage = computed(() => route.path === '/');

    const isMobileMenuOpen = ref(false);
    const scrollPosition = ref(0);
    const header = ref(null);
    const isHeaderHidden = ref(false); // 헤더 상태 추적 플래그

    const isScrolled = computed(() => scrollPosition.value > 100);

    const computedOpacity = computed(() => {
      return Math.max(0.8, 1 - scrollPosition.value / 300);
    });

    const headerBackground = computed(() => {
      if (isIndexPage.value) {
        return isScrolled.value ? 'rgba(255, 255, 255, 0.99)' : 'rgba(255, 255, 255, 0)';
      } else {
        return `rgba(255, 255, 255, ${computedOpacity.value})`;
      }
    });

    const textColor = computed(() => {
      if (isIndexPage.value) {
        return isScrolled.value ? '#2f2f2f' : '#ffffff';
      } else {
        return '#2f2f2f';
      }
    });

    const logoSrc = computed(() => {
      if (isIndexPage.value) {
        return isScrolled.value ? asLogo : asLogoWhite;
      } else {
        return asLogo;
      }
    });

    const hamburgerMenuColor = computed(() => {
      if (isIndexPage.value) {
        return isScrolled.value ? '#2f2f2f' : '#ffffff';
      } else {
        return '#2f2f2f';
      }
    });

    const toggleMobileMenu = () => {
      isMobileMenuOpen.value = !isMobileMenuOpen.value;
    };

    const handleScroll = () => {
      scrollPosition.value = window.scrollY;
    };

    onMounted(() => {
      window.addEventListener("scroll", handleScroll);

      let lastScrollY = window.scrollY;

      ScrollTrigger.create({
        start: 0,
        end: "max",
        onUpdate: () => {
          const currentScrollY = window.scrollY;
          console.log(`currentScrollY: ${currentScrollY}, lastScrollY: ${lastScrollY}, isHeaderHidden: ${isHeaderHidden.value}`);
          
          if (currentScrollY > lastScrollY && currentScrollY > 100 && !isHeaderHidden.value) {
            // 스크롤 다운: 헤더 숨기기
            gsap.to(header.value, { yPercent: -100, duration: 0.8, ease: "power3.out" });
            isHeaderHidden.value = true;
          } else if (currentScrollY < lastScrollY && isHeaderHidden.value) {
            // 스크롤 업: 헤더 나타내기
            gsap.to(header.value, { yPercent: 0, duration: 0.8, ease: "power3.out" });
            isHeaderHidden.value = false;
          }
          lastScrollY = currentScrollY;
        },
        markers: false, // 디버깅용 마커 표시 (문제 해결 후 false로 변경)
        invalidateOnRefresh: true,
      });
    });

    onBeforeUnmount(() => {
      window.removeEventListener("scroll", handleScroll);
      ScrollTrigger.getAll().forEach(trigger => trigger.kill());
    });

    // 라우트가 변경될 때 ScrollTrigger를 업데이트
    watch(route, () => {
      ScrollTrigger.refresh();
    });

    return {
      isMobileMenuOpen,
      toggleMobileMenu,
      computedOpacity,
      header,
      isIndexPage,
      textColor,
      logoSrc,
      headerBackground,
      hamburgerMenuColor, // 추가된 컴퓨티드 프로퍼티
    };
  },
};
</script>


<style scoped>
.header {
  box-sizing: border-box;
  width: 100%;
  height: 100px;
  position: fixed;
  display: flex;
  align-content: center;
  top: 0;
  left: 0;
  z-index: 1000;
  transition: background-color 0.3s ease-in-out, backdrop-filter 0.3s ease-in-out;
  backdrop-filter: blur(10px); /* 배경 블러 효과 */
}

/* 내부 컨테이너 */
.container {
  width: 100%;
  max-width: 1592px;
  margin: 0 auto;
  display: flex;
  align-items: center;
  justify-content: space-between;
  box-sizing: border-box;
}

/* 메뉴 스타일 */
.menu-list {
  list-style: none;
  display: flex;
  gap: 72px;
  padding: 0;
  margin: 0;
}

.menu-list a {
  text-decoration: none;
  /* color 속성은 템플릿에서 동적으로 설정 */
  font-size: 18px;
  font-weight: 500;
  transition: color 0.4s;
}

.menu-list a:hover {
  color: #0dd36b;
}

/* 햄버거 메뉴 스타일 */
.hamburger-menu {
  display: none;
  flex-direction: column;
  gap: 5px;
  cursor: pointer;
}

.hamburger-menu span {
  display: block;
  width: 24px;
  height: 2px;
  /* background-color: #2f2f2f; */ /* 동적으로 설정되므로 제거 */
  transition: all 0.3s;
}

/* 로고와 햄버거 메뉴 정렬 */
.logo,
.hamburger-menu {
  display: flex;
  align-items: center;
}

/* 모달 메뉴 스타일 */
.modal-menu {
  position: fixed;
  top: 0;
  left: 0;
  width: 100%;
  height: 100vh;
  background-color: rgba(0, 0, 0, 0.8); /* 반투명 검정 배경 */
  backdrop-filter: blur(15px); /* 배경 블러 처리 */
  -webkit-backdrop-filter: blur(15px); /* Safari 지원 */
  display: flex;
  align-items: center;
  justify-content: center;
  z-index: 9999;
  overflow: hidden; /* 스크롤 방지 */
  transition: 0.7s ease-in;
}

.modal-menu-list {
  display: flex;
  flex-direction: column; /* 세로 정렬 */
  gap: 20px;
  background: none; /* 배경 제거 */
  list-style: none;
  padding: 0;
  margin: 0;
  text-align: center;
}

.modal-menu-list li a {
  text-decoration: none;
  color: #fff; /* 메뉴 항목 흰색 */
  font-size: 24px;
  font-weight: bold;
}

.modal-menu-list li a:hover {
  color: #0dd36b; /* 메뉴 항목 호버 효과 */
}

.close-button {
  position: absolute;
  top: 12px;
  right: 20px;
  background: none;
  border: none;
  color: #fff;
  font-size: 32px;
  font-weight: 300;
  cursor: pointer;
}

.close-button:hover {
  color: #0dd36b;
}

/* 모달 애니메이션 */
.modal-enter-active,
.modal-leave-active {
  transition: opacity 0.7s ease, transform 0.7s ease;
}

.modal-enter {
  opacity: 0;
  transform: scale(0.9); /* 살짝 작게 시작 */
}

.modal-leave-to {
  opacity: 0;
  transform: scale(1.05); /* 살짝 크게 사라짐 */
}

/* 반응형 스타일 */
@media (max-width: 1592px) {
  .container {
    padding: 0 40px;
  }

  .menu-list {
    display: none;
  }

  .hamburger-menu {
    display: flex;
  }
}

@media (min-width: 1593px) {
  .hamburger-menu {
    display: none;
  }
}

@media (max-width: 768px) {
  .header {
    height: 72px; /* 헤더 높이 줄이기 */
  }

  .logo {
    justify-content: flex-start; /* 로고를 왼쪽 정렬 */
  }

  .logo img {
    height: 24px; /* 모바일 환경에서 로고 높이 설정 */
    vertical-align: middle; 
  }

  .container {
    padding: 0 20px; /* 모바일에서 더 작은 패딩 */
  }
  .hamburger-menu {
    display: flex;
    flex-direction: column;
    gap: 4px;
    cursor: pointer;
  }

  .hamburger-menu span {
    display: block;
    width: 20px;
    height: 2px;
    background-color: #2f2f2f; /* 기본 색상, 동적으로 덮어씌워질 예정 */
  }
}
</style>
